import React, { memo, useCallback, useEffect } from 'react';

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Glyphicon } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import {
  MobileListItem,
  MobileListWrapper,
} from '../../utils/Styles/MobileListItem';
import NoImage from '../../../NoImage.png';
import { City } from '../../../types/City';
import { getItineraryTemplates } from '../../../services/templates';
import { createSlug } from '../../../infrastructure/utils/common';
import {
  selectCityItineraryTemplates,
  selectAreCityItineraryTemplatesLoading,
  selectHaveCityItineraryTemplatesLoaded,
  fetchCityTemplateAction,
  setCityTemplateAction,
} from '../../../reduces/itineraryTemplates';
import { RootState } from '../../../reduces/reducer';
import { useAppDispatch } from '../../../store';

const StyledImage = styled.img`
  width: 100px;
  height: auto;
`;

const StyledInfoBlock = styled.div`
  flex: 1;
  margin-left: 5px;

  .title {
    margin-bottom: 10px;
    margin-right: 0;
    max-width: 100%;
    i {
      margin-left: 7px;
      color: #000000;
    }
  }
`;

type Props = {
  cities: City[];
  handleOnExploreClick(event: KeyboardEvent, name: string): void;
  handleOnGuideClick(event: KeyboardEvent, name: string): void;
};

type CityItemProps = {
  item: City;
  handleOnExploreClick(event: KeyboardEvent, name: string): void;
  handleOnGuideClick(event: KeyboardEvent, name: string): void;
};

const CityItem = memo(
  ({ item, handleOnExploreClick, handleOnGuideClick }: CityItemProps) => {
    const {
      name,
      status,
      picture: { data },
    } = item;
    const slug = createSlug(name);
    const dispatch = useAppDispatch();
    const itineraryTemplates = useSelector((state: RootState) =>
      selectCityItineraryTemplates(state, slug)
    );
    const isTemplateLoading = useSelector((state: RootState) =>
      selectAreCityItineraryTemplatesLoading(state, slug)
    );
    const hasTemplateLoaded = useSelector((state: RootState) =>
      selectHaveCityItineraryTemplatesLoaded(state, slug)
    );

    const fetchCityTemplates = useCallback(async () => {
      if (!isTemplateLoading && !hasTemplateLoaded) {
        try {
          dispatch(fetchCityTemplateAction(slug));
          const template = await getItineraryTemplates(slug);
          dispatch(setCityTemplateAction(slug, template));
        } catch (error) {}
      }
    }, [dispatch, hasTemplateLoaded, isTemplateLoading, slug]);

    useEffect(() => {
      fetchCityTemplates();
    }, [fetchCityTemplates]);

    return (
      <MobileListItem key={name}>
        <div>
          <StyledImage
            src={data ?? NoImage}
            alt='...'
            className='img-thumbnail'
            onError={e => {
              e.target.onerror = null;
              e.target.src = NoImage;
            }}
          />
        </div>
        <StyledInfoBlock>
          <h5
            className='title'
            onClick={e =>
              handleOnExploreClick((e as unknown) as KeyboardEvent, name)
            }
          >
            {name}
          </h5>
          <i
            className='fas fa-pencil-alt'
            onClick={e =>
              handleOnGuideClick((e as unknown) as KeyboardEvent, name)
            }
          />
          <div className='item'>{status}</div>
          {isTemplateLoading ? (
            <Glyphicon glyph='refresh' className='spinning' />
          ) : (
            <Link to={`/cities/${createSlug(name)}/itinerary-templates`}>
              <h5 className='item'>
                {itineraryTemplates.length ? 'Edit' : 'Create'} Itinerary
                Templates
              </h5>
            </Link>
          )}
        </StyledInfoBlock>
      </MobileListItem>
    );
  }
);

const CitiesMobile = ({
  cities = [],
  handleOnGuideClick,
  handleOnExploreClick,
}: Props) => {
  const renderCity = (city: City) => (
    <CityItem
      key={city.name + city.order}
      item={city}
      handleOnGuideClick={handleOnGuideClick}
      handleOnExploreClick={handleOnExploreClick}
    />
  );

  return <MobileListWrapper>{cities.map(renderCity)}</MobileListWrapper>;
};

export default CitiesMobile;
